import { loadRemote } from '@module-federation/runtime';
import { Box, CircularProgress } from '@mui/material';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { TrayType } from '@peritasai/types';
import React, { useMemo } from 'react';
import { UserRole, useAuthContext } from '../../features/auth';
import { PERIVERSE_HEADER_HEIGHT_PX, useHostChrome } from '../../shared';

interface PeriTrayRemoteProps {
  allowedTrayTypes?: TrayType[];
}

// Define a type for the expected structure of the remote module
type RemoteComponentModule = {
  default: React.ComponentType<PeriTrayRemoteProps>;
};

/**
 * TRAY_ASSEMBLER covers assembly of both tray types — OEM check-in
 * (TRAY_SCANNER) is a separate workflow from OEM assembly, so a
 * TRAY_SCANNER-only user is scoped to OEM only.
 */
export function computeAllowedTrayTypes(
  hasRole: (role: UserRole) => boolean
): TrayType[] {
  const types: TrayType[] = [];
  if (hasRole(UserRole.ADMIN) || hasRole(UserRole.TRAY_ASSEMBLER)) {
    types.push(TrayType.HOSPITAL);
  }
  if (
    hasRole(UserRole.ADMIN) ||
    hasRole(UserRole.TRAY_ASSEMBLER) ||
    hasRole(UserRole.TRAY_SCANNER)
  ) {
    types.push(TrayType.OEM);
  }
  return types;
}

const loadPeriTrayModule = () => loadRemote('peri_tray/PeriTray');

// Wrap the loader function with React.lazy
const PeriTrayModule = React.lazy(async () => {
  const remoteModule = (await loadPeriTrayModule()) as RemoteComponentModule;
  return { default: remoteModule.default };
});

export const Route = createFileRoute('/_auth/assemble-tray')({
  staticData: {
    title: 'pages.assembleTray.title',
    description: 'pages.assembleTray.description',
  },
  beforeLoad: async ({ context }) => {
    const { isAuthenticated, hasRole } = context.auth;

    if (!isAuthenticated) throw redirect({ to: '/' });
    // Single entry point for both hospital and OEM tray identification and
    // assembly — a user with either permission can reach it. Derived from the
    // same allow-list the screen itself uses to decide which tray types to
    // show, so this gate can't drift out of sync with that filtering.
    if (computeAllowedTrayTypes(hasRole).length === 0) {
      throw redirect({ to: '/403' });
    }
  },
  component: AssembleTrayPage,
});

function AssembleTrayPage() {
  const { trayAssemblyFullBleed } = useHostChrome();
  const { hasRole } = useAuthContext();
  const contentHeight = trayAssemblyFullBleed
    ? '100vh'
    : `calc(100vh - ${PERIVERSE_HEADER_HEIGHT_PX}px)`;

  const allowedTrayTypes = useMemo(
    () => computeAllowedTrayTypes(hasRole),
    [hasRole]
  );

  return (
    <React.Suspense
      fallback={
        <Box
          sx={{
            width: '100%',
            height: contentHeight,
            maxHeight: contentHeight,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      }
    >
      <Box
        sx={{
          width: '100%',
          height: contentHeight,
          maxHeight: contentHeight,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <PeriTrayModule allowedTrayTypes={allowedTrayTypes} />
      </Box>
    </React.Suspense>
  );
}
